import type { Invoice } from '@/types/invoice';

/**
 * Base template for mock Invoice data based on real invoice structure
 * Using empty strings and default values instead of null to match Invoice type
 */
const getBaseInvoiceTemplate = (): Omit<Invoice, 'InvoiceID' | 'Invoice' | 'TATransactionID' | 'ProductID'> => ({
	DateCreated: new Date('2024-11-05T17:14:48.000Z'),
	LastModDate: new Date('2024-11-05T17:14:48.000Z'),
	ModifiedBy: 'QB Admin',
	PhysicianName: 'Joe Ford',
	EMRID: '217726',
	ProcedureDOS: new Date('2025-04-13'),
	Unit: 2,
	InvoiceCost: 1000,
	Product: 'Axolotl Dual Graft (Q4332)',
	Clinic: 'Suture Self Center',
	QCode: 'Q4332',
	Invoicecollected: 0,
	InvoiceStatus: 'Past Due',
	CheckNum: '',
	PaidDate: new Date('2025-06-04'),
	GrossMargin: 0,
	Notes: '',
	InvoiceDate: '2025-08-31T00:00:00.000Z',
	INVOICEDUEDATE: '2025-07-20T00:00:00.000Z',
	Aging: 15,
	SALESGROUP: '',
	Manufacturer: 'Imbed',
	RebateAmt: 0,
	PatientFirstName: 'Zoro',
	PatientLastName: 'Alex Wright',
	PatientFullName: 'Zoro Alex Wright',
	SalesRep1: 'Bruce Wayne',
	MasterRep: '',
	SalesRep2: '',
	TalarRep: '',
	SalesRep3: '',
	SalesRepAName: '',
	SalesRepbName: '',
	SalesRepcName: '',
	MasterRepName: '',
	AccountManager: '',
	State: 'GA',
	RepPaymentDisposition: 'Commission Due',
	SlidingScale: '',
	Aging_Range: '31-60 Days',
	eCW_EMRID: '',
	primaryInsurance: 'UPMC For Life',
	billingsoftware: '',
	postingdate: new Date(),
	macregion: '',
	Producttype: 'Skin Graft',
	NPI: '',
	Commissionable: 'True',
	QBAmtDue: 1000,
	Account_ID: '{09253dcf-8729-4bf0-99ba-75661eac0f7a}',
	trackingnumber: '',
	pendingPayment: false,
	clinicQBOId: '',
	subInvoices: [],
	isPaidOnQBO: false,
});

/**
 * Creates mock Invoice data for the tutorial
 * @param index - The index of the mock Invoice to create (0-9)
 * @returns A mock Invoice object
 */
export const createMockInvoice = (index: number): Invoice => {
	const baseDate = new Date('2025-01-15');
	const invoiceDate = new Date(baseDate);
	invoiceDate.setDate(invoiceDate.getDate() - index * 10);

	const dueDate = new Date(invoiceDate);
	dueDate.setDate(dueDate.getDate() + 30);

	// Product variations
	const products = [
		{ name: 'Axolotl Dual Graft (Q4332)', qCode: 'Q4332', type: 'Skin Graft' },
		{ name: 'Amnio-Maxx (Q4239)', qCode: 'Q4239', type: 'Amniotic Membrane' },
		{ name: 'Helicoll (Q4164)', qCode: 'Q4164', type: 'Collagen Matrix' },
		{ name: 'DermaMatrix (Q4123)', qCode: 'Q4123', type: 'Dermal Matrix' },
		{ name: 'AlloDerm (Q4056)', qCode: 'Q4056', type: 'Acellular Dermis' },
	];

	// Clinic variations
	const clinics = [
		'Suture Self Center',
		'Podiatry Care Clinic',
		'Advanced Foot & Ankle',
		'Coastal Podiatry Group',
		'Metro Foot Specialists',
	];

	// Physician variations
	const physicians = [
		'Joe Ford',
		'Dr. Sarah Johnson',
		'Dr. Michael Chen',
		'Dr. Emily Rodriguez',
		'Dr. David Thompson',
	];

	// Patient name variations
	const patients = [
		{ first: 'Zoro', last: 'Alex Wright' },
		{ first: 'John', last: 'Smith' },
		{ first: 'Jane', last: 'Williams' },
		{ first: 'Robert', last: 'Brown' },
		{ first: 'Maria', last: 'Garcia' },
	];

	// Sales rep variations
	const salesReps = [
		'Bruce Wayne',
		'Clark Kent',
		'Diana Prince',
		'Barry Allen',
		'Arthur Curry',
	];

	// Insurance variations
	const insurances = [
		'UPMC For Life',
		'Blue Cross Blue Shield',
		'Aetna',
		'Cigna',
		'UnitedHealthcare',
	];

	// Status variations
	const statuses = ['Past Due', 'Open', 'Paid', 'Pending'];
	const status = statuses[index % statuses.length];
	const isPaid = status === 'Paid';

	// Aging range variations
	const agingRanges = ['0-30 Days', '31-60 Days', '61-90 Days', '91+ Days'];
	const agingRange = agingRanges[index % agingRanges.length];

	const product = products[index % products.length];
	const clinic = clinics[index % clinics.length];
	const physician = physicians[index % physicians.length];
	const patient = patients[index % patients.length];
	const salesRep = salesReps[index % salesReps.length];
	const insurance = insurances[index % insurances.length];

	const invoiceId = String(1723 + index);
	const taTransactionId = `TA-0012354${index + 1}`;
	const productId = taTransactionId;
	const emrId = String(217726 + index);

	const baseTemplate = getBaseInvoiceTemplate();

	return {
		...baseTemplate,
		InvoiceID: invoiceId,
		Invoice: invoiceId,
		TATransactionID: taTransactionId,
		ProductID: productId,
		DateCreated: new Date(invoiceDate.getTime() - 30 * 24 * 60 * 60 * 1000), // 30 days before invoice date
		LastModDate: new Date(invoiceDate.getTime() - 20 * 24 * 60 * 60 * 1000), // 20 days before invoice date
		PhysicianName: physician,
		EMRID: emrId,
		ProcedureDOS: new Date(invoiceDate.getTime() - 5 * 24 * 60 * 60 * 1000), // 5 days before invoice date
		Unit: index % 3 + 1, // 1, 2, or 3
		InvoiceCost: 1000 + index * 150,
		Product: product.name,
		Clinic: clinic,
		QCode: product.qCode,
		Invoicecollected: isPaid ? 1000 + index * 150 : 0,
		InvoiceStatus: status,
		CheckNum: isPaid ? `CHK-${index + 1}` : '',
		PaidDate: isPaid ? new Date(invoiceDate.getTime() + 10 * 24 * 60 * 60 * 1000) : new Date(),
		GrossMargin: Math.round((1000 + index * 150) * 0.3), // 30% margin
		InvoiceDate: invoiceDate.toISOString(),
		INVOICEDUEDATE: dueDate.toISOString(),
		Aging: 15 + index * 5,
		Manufacturer: 'Imbed',
		PatientFirstName: patient.first,
		PatientLastName: patient.last,
		PatientFullName: `${patient.first} ${patient.last}`,
		SalesRep1: salesRep,
		RepPaymentDisposition: isPaid ? 'Commission Paid' : 'Commission Due',
		Aging_Range: agingRange,
		primaryInsurance: insurance,
		Producttype: product.type,
		QBAmtDue: isPaid ? 0 : 1000 + index * 150,
		Account_ID: '{09253dcf-8729-4bf0-99ba-75661eac0f7a}',
		pendingPayment: status === 'Pending',
		subInvoices: [
			{
				...baseTemplate,
				InvoiceID: invoiceId,
				Invoice: invoiceId,
				TATransactionID: taTransactionId,
				ProductID: productId,
				Product: product.name,
				QCode: product.qCode,
				InvoiceCost: 1000 + index * 150,
				QBAmtDue: isPaid ? 0 : 1000 + index * 150,
				pendingPayment: status === 'Pending',
			},
		],
	};
};

/**
 * Generates an array of mock Invoice data for the tutorial
 * @param count - Number of mock Invoices to generate (default: 10)
 * @returns Array of mock Invoice objects
 */
export const generateMockInvoiceData = (count = 10): Invoice[] => {
	return Array.from({ length: count }, (_, index) => createMockInvoice(index));
};

